import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Col, Row, Space, Spin } from 'antd'
import { Input } from 'antd'
import ImageSwapFigure from '../exercise/ImageSwapFigure'
import { overriddenDot } from '../schedule/tracpatch.config.styles'
import {
  PROGRESS_GOAL_WEEKS,
  resolveGoalCell,
  isGoalCellOverridden,
  setGoalCell,
} from '../schedule/progress.goals.defaults'
import {
  fetchClinicSetting,
  saveClinicSetting,
  resetClinicSetting,
} from '../../store/action/settings.action'

const SETTING_KEY = 'progress-test-goals'

const tableHeaderStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#64748b',
  whiteSpace: 'nowrap',
}

// Frame conversion, mirroring the per-patient ProgressGoalsModal so the preview
// dial matches the patient view exactly. Goal values are DISPLAYED/clinical
// degrees (0° = full extension, 90° = seated rest). The figure + dial run in an
// internal frame = measured + PROGRESS_DEGREE_OFFSET (rest lands on internal 45).
const PROGRESS_DEGREE_OFFSET = 45
const PROGRESS_REST_DISPLAY_DEG = 90
const toMeasured = (d) =>
  d >= PROGRESS_REST_DISPLAY_DEG
    ? d - PROGRESS_REST_DISPLAY_DEG
    : (d - PROGRESS_REST_DISPLAY_DEG) / 2
const toInternal = (d) => toMeasured(d) + PROGRESS_DEGREE_OFFSET

// A single goal cell. Renders as a plain text box (no spinner arrows) so it
// behaves like any other input — you can backspace it empty and retype. While
// focused it holds its own draft string, which is what lets the field go empty
// even though clearing it removes the override and re-resolves the cell to its
// (non-empty) default. On blur, focus leaves and the resolved value shows again.
// Empty commits null (restores the default); digits commit an integer degree.
function GoalCell(props) {
  const { value, overridden, onChange, onFocus } = props
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')
  const display = focused ? draft : value != null ? String(value) : ''
  return (
    <Input
      value={display}
      inputMode='numeric'
      onFocus={() => {
        setDraft(value != null ? String(value) : '')
        setFocused(true)
        if (onFocus) onFocus()
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, '')
        setDraft(raw)
        onChange(raw === '' ? null : parseInt(raw, 10))
      }}
      onBlur={() => setFocused(false)}
      style={
        overridden
          ? { width: '100%', borderColor: '#1890ff' }
          : { width: '100%' }
      }
    />
  )
}

// Clinic-scoped editor for Progress-Test per-week ROM goals (extension +
// flexion). Saves all overridden cells under a single ClinicSettings row keyed
// by 'progress-test-goals'. Mirrors the per-patient ProgressGoalsModal table +
// avatar preview, and the TracpatchExerciseSettingsForm clinic-settings pattern.
function ProgressTestGoalsSettingsForm(props) {
  // customerid (clinic page) or companyid (company page: what a new clinic
  // starts from) — the settings actions route on whichever is present.
  const { customerid, companyid } = props
  const scopeId = customerid || companyid
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // overrides: array of { week, extension, flexion } — sparse, only overridden.
  const [overrides, setOverrides] = useState([])
  const [selectedWeek, setSelectedWeek] = useState(PROGRESS_GOAL_WEEKS[0])
  // Preview angle is a DISPLAYED clinical angle; starts at seated rest (90°).
  const [previewAngle, setPreviewAngle] = useState(PROGRESS_REST_DISPLAY_DEG)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const clinicRes = await props.fetchClinicSetting({
          customerid,
          companyid,
          key: SETTING_KEY,
        })
        if (cancelled) return
        const clinicValue =
          (clinicRes && clinicRes.data && clinicRes.data.value) || null
        const loaded =
          clinicValue && Array.isArray(clinicValue.overrides)
            ? clinicValue.overrides
            : []
        setOverrides(loaded)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (scopeId) load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId])

  const cellValue = (week, field) => resolveGoalCell(week, field, overrides)
  const cellOverridden = (week, field) =>
    isGoalCellOverridden(week, field, overrides)
  const setCell = (week, field, value) => {
    setOverrides((prev) => setGoalCell(prev, week, field, value))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await props.saveClinicSetting({
        customerid,
        companyid,
        key: SETTING_KEY,
        value: { overrides },
      })
      if (res && !res.error && props.onSaved) props.onSaved()
    } finally {
      setSaving(false)
    }
  }

  const handleResetAll = async () => {
    setSaving(true)
    try {
      await props.resetClinicSetting({ customerid, companyid, key: SETTING_KEY })
      setOverrides([])
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  // The selected week's resolved goals (DISPLAYED degrees) become dotted-arc
  // weekly-goal markers on the dial — same UI as the patient Progress Test.
  const goalExt = cellValue(selectedWeek, 'extension')
  const goalFlex = cellValue(selectedWeek, 'flexion')
  const goalAngles = []
  if (goalExt != null) goalAngles.push(toInternal(goalExt))
  if (goalFlex != null) goalAngles.push(toInternal(goalFlex))
  const figureAngle = toInternal(previewAngle)
  const sliderMin = 0
  const sliderMax = 160

  return (
    <div>
      <Row gutter={16} align='middle' style={{ marginBottom: 16 }}>
        <Col flex='auto'>
          <div style={{ fontSize: 12, color: '#666' }}>
            {customerid
              ? "These goals apply to every patient in this clinic. Weeks you don't"
              : "These goals become the starting goals of every clinic created from now on. Weeks you don't"}{' '}
            override fall back to MoveAI's default goal table.
          </div>
        </Col>
        <Col>
          <Space>
            <Button onClick={handleResetAll} disabled={saving}>
              Reset all
            </Button>
            {props.onCancel && (
              <Button onClick={props.onCancel} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button type='primary' onClick={handleSave} loading={saving}>
              Save
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ flexWrap: 'nowrap' }}>
        <Col flex='1 1 0' style={{ minWidth: 0 }}>
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <Row
              gutter={8}
              style={{
                background: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
                padding: '10px 9px 10px 12px',
                alignItems: 'center',
              }}
            >
              <Col span={8}><div style={tableHeaderStyle}>Week</div></Col>
              <Col span={8}><div style={tableHeaderStyle}>Extension (°)</div></Col>
              <Col span={8}><div style={tableHeaderStyle}>Flexion (°)</div></Col>
            </Row>
            {PROGRESS_GOAL_WEEKS.map((week, idx) => {
              const isSelected = selectedWeek === week
              const isLast = idx === PROGRESS_GOAL_WEEKS.length - 1
              const overridden =
                cellOverridden(week, 'extension') ||
                cellOverridden(week, 'flexion')
              const cell = (field) => (
                <GoalCell
                  value={cellValue(week, field)}
                  overridden={cellOverridden(week, field)}
                  onChange={(val) => setCell(week, field, val)}
                  onFocus={() => setSelectedWeek(week)}
                />
              )
              return (
                <div
                  key={week}
                  onClick={() => setSelectedWeek(week)}
                  style={{
                    cursor: 'pointer',
                    background: isSelected ? '#f0fdf4' : '#fff',
                    borderLeft: isSelected
                      ? '3px solid #22c55e'
                      : '3px solid transparent',
                    borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                    padding: '10px 9px 10px 12px',
                  }}
                >
                  <Row gutter={8} style={{ alignItems: 'center' }}>
                    <Col span={8}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: isSelected
                            ? '#15803d'
                            : overridden
                            ? '#1890ff'
                            : '#1f2937',
                          display: 'flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {week === -1 ? 'Pre-Op' : `Week ${week}`}
                        {overridden ? <span style={overriddenDot} /> : null}
                      </div>
                    </Col>
                    <Col span={8}>{cell('extension')}</Col>
                    <Col span={8}>{cell('flexion')}</Col>
                  </Row>
                </div>
              )
            })}
          </div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
            Each cell defaults to the standard recovery goal for that week. Edit
            a cell to override it for {customerid ? 'this clinic' : 'new clinics'}; clearing a cell restores the
            default. Values are clinical degrees (0° = full extension, 90° =
            seated rest). Click a row to preview it.
          </div>
        </Col>

        <Col
          flex='0 0 340px'
          style={{ alignSelf: 'flex-start', position: 'sticky', top: 0 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 6,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
              PREVIEW
            </div>
            <div style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>
              {selectedWeek === -1 ? 'Pre-Op' : `Week ${selectedWeek}`}
            </div>
          </div>
          <div
            style={{
              background: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 0,
              fontFamily: 'Roboto, sans-serif',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 18px 6px' }}>
              <ImageSwapFigure
                assetKind='HEEL_SLIDE_ROM'
                liveAngle={figureAngle}
                labelAngle={previewAngle}
                bandRanges={{ yellow: [0, 130] }}
                yellowOutline
                goalAngles={goalAngles}
                maxWidth={240}
              />
            </div>

            <div
              style={{
                margin: '0 14px 10px',
                padding: '8px 10px',
                background: '#fff8e1',
                border: '1px solid #ffe9a8',
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#8a6d3b',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                <span>PREVIEW ANGLE</span>
                <span>{Math.round(previewAngle != null ? previewAngle : 0)}°</span>
              </div>
              <input
                type='range'
                min={sliderMin}
                max={sliderMax}
                step={1}
                value={previewAngle != null ? previewAngle : 0}
                onChange={(e) => setPreviewAngle(parseFloat(e.target.value))}
                style={{ width: '100%', transform: 'scaleX(-1)' }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#a08850',
                  marginTop: 2,
                }}
              >
                <span>← flexion ({sliderMax}°)</span>
                <span>extension ({sliderMin}°) →</span>
              </div>
            </div>
          </div>

          <div
            style={{ fontSize: 11, color: '#888', marginTop: 8, lineHeight: 1.5 }}
          >
            Goal ({selectedWeek === -1 ? 'pre-op' : `week ${selectedWeek}`}):{' '}
            extension <b>{goalExt != null ? `${goalExt}°` : '—'}</b> · flexion{' '}
            <b>{goalFlex != null ? `${goalFlex}°` : '—'}</b>
          </div>
        </Col>
      </Row>
    </div>
  )
}

ProgressTestGoalsSettingsForm.propTypes = {
  customerid: PropTypes.string,
  companyid: PropTypes.string,
  onSaved: PropTypes.func,
  onCancel: PropTypes.func,
}

const mapDispatchToProps = (dispatch) => ({
  fetchClinicSetting: (params) => dispatch(fetchClinicSetting(params)),
  saveClinicSetting: (params) => dispatch(saveClinicSetting(params)),
  resetClinicSetting: (params) => dispatch(resetClinicSetting(params)),
})

export default connect(null, mapDispatchToProps)(ProgressTestGoalsSettingsForm)
